import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import {
  fetchOnCallActions,
  fetchOnCallAction,
} from '@/api/onCallActionsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList, unwrapApiPayload } from '@/utils/graphql'
import {
  normalizeOnCallActionsList,
  normalizeOnCallAction,
} from '@/utils/onCallActions'
import {
  readChatbotDetailsFromOrg,
  readGreetingMessageFromOrg,
  readSystemPromptFromOrg,
} from '@/utils/agentOtherSettings'

export function getOnCallActionsErrorMessage(payload) {
  return getApiMessage(payload, 'Failed to load on-call actions')
}

export function useOnCallActionsData(activeRef, options = {}) {
  const store = useStore()
  const { onError } = options

  const actions = ref([])
  const loading = ref(false)

  const isSessionReady = computed(() => store.getters.isSessionReady)
  const sessionContext = computed(() => store.getters.sessionContext)
  const organization = computed(() => store.state.organization || {})

  const actionRows = computed(() => actions.value)
  const totalActions = computed(() => actionRows.value.length)
  const activeCount = computed(
    () => actionRows.value.filter((row) => row.is_active).length,
  )

  const orgContext = computed(() => {
    const { org_id, current_user_id } = sessionContext.value ?? {}
    const org = organization.value || {}
    const chatbot = readChatbotDetailsFromOrg(org)

    return {
      org_id,
      current_user_id: String(current_user_id || '').trim(),
      assigned_sip_number: String(
        org.assigned_sip_number || org.sip_number || '',
      ).trim(),
      org_name: String(org.org_name || '').trim(),
      org_website_url: String(org.org_website_url || '').trim(),
      org_contact_email: String(org.org_contact_email || '').trim(),
      org_contact_name: String(org.org_contact_name || '').trim(),
      greeting_message: readGreetingMessageFromOrg(org),
      system_prompt: readSystemPromptFromOrg(org),
      chatbot_name: chatbot.chatbot_name,
      chatbot_language: chatbot.chatbot_language,
      chatbot_details: org.chatbot_details,
      organization: org,
    }
  })

  function reportLoadError(payload) {
    const message = getOnCallActionsErrorMessage(payload)
    onError?.(message)
    return message
  }

  async function loadActions() {
    if (!isSessionReady.value) return { ok: false }

    loading.value = true
    try {
      const response = await fetchOnCallActions()

      if (!isApiSuccess(response)) {
        actions.value = []
        const error = reportLoadError(response)
        return { ok: false, error }
      }

      const rows = unwrapApiList(response)
      actions.value = normalizeOnCallActionsList(rows)
      return { ok: true }
    } catch (err) {
      actions.value = []
      const error = reportLoadError(err)
      return { ok: false, error }
    } finally {
      loading.value = false
    }
  }

  async function loadAction(actionId) {
    if (!actionId) return null

    const response = await fetchOnCallAction({
      action_id: actionId,
    })

    if (!isApiSuccess(response)) {
      throw new Error(getOnCallActionsErrorMessage(response))
    }

    const row = unwrapApiPayload(response)
    if (row && typeof row === 'object') {
      return normalizeOnCallAction(row)
    }

    return null
  }

  watch(
    isSessionReady,
    (ready) => {
      if (ready && activeRef?.value) void loadActions()
    },
    { immediate: true },
  )

  watch(activeRef, (isActive) => {
    if (isActive && isSessionReady.value) void loadActions()
  })

  return {
    loading,
    isSessionReady,
    actionRows,
    totalActions,
    activeCount,
    orgContext,
    loadActions,
    loadAction,
  }
}
